import type { CitiesDto, CountriesDto } from '@carcheck/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * The public place dictionary (AUT-761) for the admin's country + city
 * pickers: `GET /public/countries` and `GET /public/cities?country=XX`.
 * It is static data, so it never goes stale within a session.
 */
export interface CountryOption {
  code: string;
  name: string;
}

/** A city as the pickers send it: `key` on the wire, `name` on screen. */
export interface CityOption {
  key: string;
  name: string;
}

const STATIC = { staleTime: Infinity, gcTime: Infinity } as const;

export const useCountries = (): UseQueryResult<CountryOption[], Error> =>
  useQuery({
    queryKey: ['public', 'countries'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<CountriesDto>('/public/countries', { signal });
      return data.items.map(({ code, name }) => ({ code, name }));
    },
    ...STATIC,
  });

/** Every city of a region list, flat; the byte-for-byte BY shape has no keys (the name is the key). */
export const flattenCities = (dto: CitiesDto): CityOption[] =>
  dto.regions.flatMap((region) =>
    region.items
      ? region.items.map(({ key, name }) => ({ key, name }))
      : region.cities.map((name) => ({ key: name, name })),
  );

export const useCities = (country: string): UseQueryResult<CityOption[], Error> =>
  useQuery({
    queryKey: ['public', 'cities', country],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<CitiesDto>('/public/cities', {
        params: { country },
        signal,
      });
      return flattenCities(data);
    },
    enabled: country !== '',
    ...STATIC,
  });

/** The on-screen name of a picked city: own text as typed, a key through the dictionary. */
export const useCityLabel = (country: string, city: string, custom: boolean): string => {
  const cities = useCities(custom || city === '' ? '' : country || 'BY');
  if (custom || city === '') return city;
  return cities.data?.find((c) => c.key === city)?.name ?? '';
};
